import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraDataTable } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEntityCell, SpecraRestrictedChip, SpecraTooltip } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { basisLabel, emptyReason, linkLabel, money, pricingLinkVariant } from './ratesView';
const T = Testids.pricingWorkspace;
export function RatesTab({ rows, selected, access, canEdit, onSelect, onSetRate, onHours }) {
    const columns = [
        {
            id: 'title',
            header: 'Section B, as written',
            cell: (r)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: r.title,
                    description: r.removedFromSectionB ? /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: "Removed by amendment"
                    }) : undefined
                })
        },
        {
            id: 'catalog',
            header: 'Our catalog',
            cell: (r)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: r.link?.decision === 'price_from_source_vehicle' ? `${r.link.sourceVehicleName || r.link.entryVehicleName} only` : r.link?.entryTitle || 'Not matched',
                    description: /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: pricingLinkVariant(r.link),
                        children: linkLabel(r.link)
                    })
                })
        },
        {
            id: 'hours',
            header: 'Hours',
            align: 'right',
            cell: (r)=>/*#__PURE__*/ _jsx(SpecraTooltip, {
                    content: `From CLIN ${r.hoursClinNumbers.join(', ')}`,
                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "ghost",
                        size: "xs",
                        "data-testid": T.hours,
                        onClick: (e)=>{
                            e.stopPropagation();
                            onHours(r, e.currentTarget);
                        },
                        children: r.hours ? Number(r.hours).toLocaleString('en-US') : '—'
                    })
                })
        },
        ...[
            'ours',
            'teammate',
            'competitor'
        ].map((kind)=>({
                id: kind,
                header: ({
                    ours: 'Ours',
                    teammate: 'Teammate',
                    competitor: 'Competitor'
                })[kind],
                align: 'right',
                cell: (r)=>access ? /*#__PURE__*/ _jsx(SpecraEntityCell, {
                        title: money(r[kind]?.amount),
                        description: r[kind] ? `${r[kind]?.sourceLabel}${r[kind]?.crossVehicle ? ' · other vehicle' : ''}` : emptyReason(r[`${kind}EmptyReason`])
                    }) : /*#__PURE__*/ _jsx(SpecraEntityCell, {
                        title: "—",
                        description: /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
                            label: "Withheld",
                            reason: "ratebook.columns.read access needed"
                        })
                    })
            })),
        {
            id: 'suggested',
            header: 'Suggested',
            align: 'right',
            cell: (r)=>/*#__PURE__*/ _jsx(SpecraTooltip, {
                    content: r.suggested?.ruleLabel || 'No suggestion',
                    children: /*#__PURE__*/ _jsx("span", {
                        children: /*#__PURE__*/ _jsx(SpecraEntityCell, {
                            title: /*#__PURE__*/ _jsx(SpecraText, {
                                tone: r.suggested?.requiresChoice ? 'muted' : undefined,
                                children: money(r.suggested?.amount)
                            }),
                            description: r.suggested?.requiresChoice ? 'ceiling, not a price' : r.suggested?.ruleId === 'R2_FUTURE_CEILING' ? 'period ceiling' : r.suggested?.amount ? 'lowest real, last 24 mo' : undefined
                        })
                    })
                })
        }
    ];
    if (rows.some((r)=>r.adjustment)) columns.push({
        id: 'adjustment',
        header: /*#__PURE__*/ _jsx(SpecraTooltip, {
            content: `Team competitive adjustment, applied to Suggested. Set by ${rows.find((r)=>r.adjustment)?.adjustment?.setByName}; reason in the row details.`,
            children: /*#__PURE__*/ _jsx(SpecraText, {
                children: "Team adj."
            })
        }),
        align: 'right',
        cell: (r)=>r.adjustment ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                content: r.adjustedAmount ? r.adjustment.reason : 'Adjustments apply to real-rate suggestions only.',
                children: /*#__PURE__*/ _jsx("span", {
                    children: /*#__PURE__*/ _jsx(SpecraEntityCell, {
                        title: money(r.adjustedAmount),
                        description: `${r.adjustment.pct}% · ${r.adjustment.scopeLabel}`
                    })
                })
            }) : null
    });
    columns.push({
        id: 'using',
        header: 'Using',
        align: 'right',
        cell: (r)=>{
            const cell = /*#__PURE__*/ _jsx(SpecraTooltip, {
                content: r.using?.reason || basisLabel(r),
                children: /*#__PURE__*/ _jsx("span", {
                    children: /*#__PURE__*/ _jsx(SpecraEntityCell, {
                        title: r.using?.amount ? money(r.using.amount) : /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "warning",
                            children: "Choose"
                        }),
                        description: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                basisLabel(r),
                                r.using?.selectionReviewStatus && /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "warning",
                                    children: "Evidence changed"
                                }),
                                r.adjustmentStale && /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "warning",
                                    children: "Adjustment changed"
                                })
                            ]
                        })
                    })
                })
            });
            return canEdit && !r.removedFromSectionB ? /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "ghost",
                className: "h-auto whitespace-normal py-0 text-right",
                "data-testid": T.using,
                onClick: (e)=>{
                    e.stopPropagation();
                    onSetRate(r);
                },
                children: cell
            }) : cell;
        }
    });
    return /*#__PURE__*/ _jsx(SpecraDataTable, {
        framed: true,
        testId: T.ratesTable,
        columns: columns,
        rows: rows,
        rowKey: (r)=>r.categoryId,
        rowTestId: T.row,
        getRowLabel: (r)=>r.title,
        onRowClick: (r)=>onSelect(r.categoryId),
        isRowSelected: (r)=>r.categoryId === selected,
        empty: /*#__PURE__*/ _jsx(SpecraText, {
            children: "No labor categories in this scenario"
        })
    });
}
